'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Box, Maximize2, Ruler } from 'lucide-react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { getMaterial, getProduct } from './configurator-data';
import { createDoorSurfaceGeometry } from './configurator-model-geometry';
import { buildConfigurationFingerprint, deriveDoorGeometry } from './door-model';
import type { ConfiguratorState, PriceEstimate } from './configurator-types';
import styles from './configurator.module.css';

export {
  createHandleCurve,
  createHandlePocketCutterGeometry,
  createSlabGeometry,
  createSweptHandleProfileCutterGeometry,
} from './configurator-model-geometry';

type PreviewBaseProps = {
  config: ConfiguratorState;
  estimate: PriceEstimate;
  dimensionView?: ReactNode;
};

export type ConfiguratorPreviewMode = 'model' | 'dimensions';

type PreviewProps = PreviewBaseProps &
  (
    | { mode?: undefined; onModeChange?: undefined }
    | {
        mode: ConfiguratorPreviewMode;
        onModeChange: (mode: ConfiguratorPreviewMode) => void;
      }
  );

type PreviewContext = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  modelRoot: THREE.Group;
  floor: THREE.Mesh;
  grid: THREE.GridHelper;
};

type GeneratedPreviewContext = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  modelRoot: THREE.Group;
  requestAnimation: () => void;
};

type GeneratedDoorPreviewProps = {
  config: ConfiguratorState;
  className?: string;
  presentation?: 'hero' | 'card';
  deferMs?: number;
};

const CSG_REBUILD_DELAY_MS = 180;
const HERO_ROTATION_HALF_ARC = THREE.MathUtils.degToRad(20);
const HERO_ROTATION_CYCLE_MS = 16_000;
const HERO_CAMERA_TARGET = new THREE.Vector3(0, -0.25, 0);
const CARD_CAMERA_POSITION = new THREE.Vector3(1.55, 1.55, 4.85);
const CARD_CAMERA_TARGET = new THREE.Vector3(0, 0.24, 0);
const CARD_SAFE_NDC_EXTENT = 0.95;

/**
 * Lightweight presentation of the same CSG model used by the editor preview.
 * Product cards stay render-on-demand; only the single hero instance receives
 * a visibility-aware animation loop and pointer interaction.
 */
export function GeneratedDoorPreview({
  config,
  className,
  presentation = 'card',
  deferMs = 0,
}: GeneratedDoorPreviewProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const contextRef = useRef<GeneratedPreviewContext | null>(null);
  const configRef = useRef(config);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const modelFingerprint = buildConfigurationFingerprint(config);
  configRef.current = config;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      setStatus('error');
      return;
    }

    configurePreviewRenderer(renderer, presentation === 'hero' ? 1.5 : 1.2, false);
    renderer.setSize(Math.max(host.clientWidth, 1), Math.max(host.clientHeight, 1), false);
    renderer.domElement.setAttribute('aria-hidden', 'true');
    renderer.domElement.tabIndex = -1;
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      presentation === 'hero' ? 32 : 35,
      Math.max(host.clientWidth, 1) / Math.max(host.clientHeight, 1),
      0.1,
      100,
    );
    if (presentation === 'hero') {
      camera.position.set(3.05, 2, 6.3);
      camera.lookAt(HERO_CAMERA_TARGET);
    } else {
      camera.position.copy(CARD_CAMERA_POSITION);
      camera.lookAt(CARD_CAMERA_TARGET);
    }
    addPreviewLights(scene, false, presentation === 'card' ? 'card' : 'standard');

    const modelRoot = new THREE.Group();
    scene.add(modelRoot);

    const render = () => renderer.render(scene, camera);
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const animationStartedAt = window.performance.now();
    let previousFrameAt = animationStartedAt;
    let animationFrame = 0;
    let currentAngle = 0;
    let pointerAngle: number | null = null;
    let isVisible = true;
    let isDisposed = false;

    const cancelAnimation = () => {
      if (!animationFrame) return;
      window.cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    };

    function queueAnimation() {
      if (
        presentation !== 'hero' ||
        animationFrame ||
        isDisposed ||
        !isVisible ||
        document.hidden ||
        modelRoot.children.length === 0
      ) {
        return;
      }
      animationFrame = window.requestAnimationFrame(animate);
    }

    function animate(now: number) {
      animationFrame = 0;
      if (isDisposed || !isVisible || document.hidden || modelRoot.children.length === 0) return;

      const automaticMotion = !motionPreference.matches;
      const targetAngle =
        pointerAngle ??
        (automaticMotion
          ? Math.sin(((now - animationStartedAt) / HERO_ROTATION_CYCLE_MS) * Math.PI * 2) *
            HERO_ROTATION_HALF_ARC
          : 0);

      const deltaSeconds = Math.min(Math.max((now - previousFrameAt) / 1000, 0), 0.1);
      previousFrameAt = now;
      currentAngle = THREE.MathUtils.damp(
        currentAngle,
        targetAngle,
        pointerAngle === null ? 3.4 : 7.7,
        deltaSeconds,
      );
      modelRoot.rotation.y = currentAngle;
      render();

      if (
        automaticMotion ||
        pointerAngle !== null ||
        Math.abs(currentAngle - targetAngle) > 0.001
      ) {
        queueAnimation();
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || motionPreference.matches) return;
      const bounds = host.getBoundingClientRect();
      const normalizedX = THREE.MathUtils.clamp(
        ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * 2 - 1,
        -1,
        1,
      );
      pointerAngle = normalizedX * HERO_ROTATION_HALF_ARC;
      queueAnimation();
    };

    const handlePointerLeave = () => {
      pointerAngle = null;
      queueAnimation();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimation();
      } else {
        queueAnimation();
      }
    };

    const handleMotionPreferenceChange = () => {
      pointerAngle = null;
      if (motionPreference.matches) {
        cancelAnimation();
        currentAngle = 0;
        modelRoot.rotation.y = 0;
        render();
      } else {
        previousFrameAt = window.performance.now();
        queueAnimation();
      }
    };
    let intersectionObserver: IntersectionObserver | null = null;

    if (presentation === 'hero') {
      host.addEventListener('pointermove', handlePointerMove);
      host.addEventListener('pointerleave', handlePointerLeave);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      motionPreference.addEventListener('change', handleMotionPreferenceChange);

      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(
          entries => {
            const entry = entries[0];
            if (!entry) return;
            isVisible = entry.isIntersecting;
            if (isVisible) {
              queueAnimation();
            } else {
              cancelAnimation();
            }
          },
          { rootMargin: '80px' },
        );
        intersectionObserver.observe(host);
      }

      queueAnimation();
    }

    contextRef.current = { renderer, scene, camera, modelRoot, requestAnimation: queueAnimation };

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      camera.aspect = Math.max(width, 1) / Math.max(height, 1);
      camera.updateProjectionMatrix();
      renderer.setSize(Math.max(width, 1), Math.max(height, 1), false);
      if (presentation === 'card' && modelRoot.children.length > 0) {
        fitCardPreviewCamera(camera, modelRoot);
      }
      render();
    });
    resizeObserver.observe(host);
    render();

    return () => {
      isDisposed = true;
      cancelAnimation();
      host.removeEventListener('pointermove', handlePointerMove);
      host.removeEventListener('pointerleave', handlePointerLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      motionPreference.removeEventListener('change', handleMotionPreferenceChange);
      intersectionObserver?.disconnect();
      resizeObserver.disconnect();
      disposeChildren(modelRoot);
      scene.remove(modelRoot);
      scene.traverse(object => disposeRenderable(object));
      scene.clear();
      renderer.renderLists.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      renderer.domElement.remove();
      contextRef.current = null;
    };
  }, [presentation]);

  useEffect(() => {
    const context = contextRef.current;
    if (!context) return;

    setStatus('loading');
    const rebuildTimer = window.setTimeout(
      () => {
        disposeChildren(context.modelRoot);
        try {
          const model = createDoorModel(configRef.current, { castShadows: false });
          positionPreviewModel(model, presentation === 'hero' ? 'hero' : 'card');
          context.modelRoot.add(model);
          if (presentation === 'card') {
            fitCardPreviewCamera(context.camera, context.modelRoot);
          }
          context.renderer.render(context.scene, context.camera);
          context.requestAnimation();
          setStatus('ready');
        } catch (error) {
          console.error('BDV generated preview CSG construction failed.', error);
          setStatus('error');
        }
      },
      Math.max(0, deferMs),
    );

    return () => window.clearTimeout(rebuildTimer);
  }, [deferMs, modelFingerprint, presentation]);

  return (
    <div
      className={
        className ? `${styles.generatedDoorPreview} ${className}` : styles.generatedDoorPreview
      }
      data-preview-state={status}
      data-preview-motion={presentation === 'hero' ? 'orbit' : undefined}
      aria-hidden="true"
    >
      <div ref={hostRef} className={styles.generatedDoorPreviewCanvas} />
      {status !== 'ready' && (
        <span className={styles.generatedDoorPreviewStatus}>
          {status === 'error' ? '3D náhľad nie je dostupný' : 'Generujem 3D náhľad…'}
        </span>
      )}
    </div>
  );
}

export function ConfiguratorPreview3D({
  config,
  estimate,
  mode,
  onModeChange,
  dimensionView,
}: PreviewProps) {
  const { t } = useTranslations();
  const shellRef = useRef<HTMLElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const contextRef = useRef<PreviewContext | null>(null);
  const renderModelRef = useRef(true);
  const [localMode, setLocalMode] = useState<ConfiguratorPreviewMode>('model');
  const [webGlError, setWebGlError] = useState(false);
  const [geometryError, setGeometryError] = useState<string | null>(null);
  const activeMode = mode ?? localMode;
  const changeMode = onModeChange ?? setLocalMode;
  const material = getMaterial(config.materialId);
  const geometry = deriveDoorGeometry(config);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      setWebGlError(true);
      return;
    }

    setWebGlError(false);
    configurePreviewRenderer(renderer, 2, true);
    renderer.setSize(host.clientWidth, host.clientHeight);
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      38,
      Math.max(host.clientWidth, 1) / Math.max(host.clientHeight, 1),
      0.1,
      100,
    );
    camera.position.set(4.8, 3.4, 6.8);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 3.2;
    controls.maxDistance = 12;
    controls.target.set(0, 0, 0);

    addPreviewLights(scene, true);

    const modelRoot = new THREE.Group();
    scene.add(modelRoot);

    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(5.2, 64),
      new THREE.ShadowMaterial({ color: 0x07100b, opacity: 0.24 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    const grid = new THREE.GridHelper(9, 18, 0x6f8178, 0x34453d);
    const gridMaterials = Array.isArray(grid.material) ? grid.material : [grid.material];
    gridMaterials.forEach(gridMaterial => {
      gridMaterial.transparent = true;
      gridMaterial.opacity = 0.24;
    });
    scene.add(grid);

    contextRef.current = { renderer, scene, camera, controls, modelRoot, floor, grid };

    let frame = 0;
    const render = () => {
      if (renderModelRef.current) {
        controls.update();
        renderer.render(scene, camera);
      }
      frame = window.requestAnimationFrame(render);
    };
    render();

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      camera.aspect = Math.max(width, 1) / Math.max(height, 1);
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    });
    resizeObserver.observe(host);

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(frame);
      disposeChildren(modelRoot);
      scene.remove(modelRoot);
      scene.traverse(object => disposeRenderable(object));
      scene.clear();
      controls.dispose();
      renderer.renderLists.dispose();
      renderer.dispose();
      renderer.domElement.remove();
      contextRef.current = null;
    };
  }, []);

  useEffect(() => {
    const context = contextRef.current;
    if (!context) return;
    renderModelRef.current = activeMode === 'model';
    context.controls.enabled = activeMode === 'model';
    if (activeMode === 'model') context.renderer.render(context.scene, context.camera);
  }, [activeMode]);

  useEffect(() => {
    const context = contextRef.current;
    if (!context || activeMode !== 'model') return;

    setGeometryError(null);
    const rebuildTimer = window.setTimeout(() => {
      try {
        const model = createDoorModel(config);
        positionPreviewModel(model, 'showcase');
        disposeChildren(context.modelRoot);
        context.modelRoot.add(model);
      } catch (error) {
        console.error('BDV configurator CSG construction failed.', error);
        setGeometryError('Návrhovú 3D geometriu sa nepodarilo bezpečne zostaviť.');
      }

      const maxDimension = Math.max(config.widthMm, config.heightMm);
      const scaledHeight = (config.heightMm / maxDimension) * 4.2;
      const floorY = -scaledHeight / 2 - 0.34;
      context.floor.position.y = floorY;
      context.grid.position.y = floorY + 0.01;
    }, CSG_REBUILD_DELAY_MS);

    return () => window.clearTimeout(rebuildTimer);
  }, [activeMode, config]);

  return (
    <section
      ref={shellRef}
      className={styles.previewShell}
      aria-label="Interaktívny náhľad a kótovanie výrobku"
    >
      <div className={styles.previewTopbar}>
        <div>
          <span className={styles.eyebrowLight}>
            {activeMode === 'model'
              ? 'CSG · referenčný tvar úchytu'
              : 'TECHNICKÁ SCHÉMA · KÓTY A PARAMETRE'}
          </span>
          <h3>
            {activeMode === 'model' ? '3D návrh frézovaného úchytu' : 'Presné referencie rozmerov'}
          </h3>
        </div>
        <div className={styles.previewActions}>
          <div className={styles.previewModeSwitch} role="group" aria-label="Režim náhľadu">
            <button
              type="button"
              className={activeMode === 'model' ? styles.previewButtonActive : styles.previewButton}
              onClick={() => changeMode('model')}
              aria-pressed={activeMode === 'model'}
            >
              <Box size={15} /> 3D
            </button>
            <button
              type="button"
              className={
                activeMode === 'dimensions' ? styles.previewButtonActive : styles.previewButton
              }
              onClick={() => changeMode('dimensions')}
              aria-pressed={activeMode === 'dimensions'}
            >
              <Ruler size={15} /> 2D
            </button>
          </div>
          <button
            type="button"
            className={styles.previewIconButton}
            onClick={() => shellRef.current?.requestFullscreen?.()}
            aria-label="Zobraziť náhľad na celú obrazovku"
          >
            <Maximize2 size={17} />
          </button>
        </div>
      </div>

      <div className={styles.previewCanvasWrap}>
        <div
          ref={hostRef}
          className={
            activeMode === 'dimensions'
              ? `${styles.previewCanvas} ${styles.previewCanvasMuted}`
              : styles.previewCanvas
          }
          aria-hidden={activeMode === 'dimensions'}
        />
        {webGlError && (
          <div className={styles.previewError} role="alert">
            <Box size={28} />
            3D náhľad nie je v tomto prehliadači dostupný.
          </div>
        )}
        {!webGlError && geometryError && (
          <div className={styles.previewError} role="alert">
            <AlertTriangle size={28} />
            <strong>3D výrez nebol vytvorený</strong>
            <span>{geometryError}</span>
          </div>
        )}
        {activeMode === 'dimensions' && (
          <div className={styles.previewDimensionsOverlay}>
            {dimensionView ?? <p>Kótovací náhľad nie je dostupný.</p>}
          </div>
        )}
      </div>

      <div className={styles.previewStats}>
        <div>
          <span>Výrobok</span>
          <strong>{getProduct(config.productId).label}</strong>
        </div>
        <div>
          <span>Hĺbka Q / zostatok</span>
          <strong>
            {formatMm(geometry.handle.depthMm)} / {formatMm(geometry.handle.remainingWallMm)} mm
          </strong>
        </div>
        <div>
          <span>{t('text4065')}</span>
          <strong>{material.label}</strong>
        </div>
        <div>
          <span>Orientačne spolu bez DPH</span>
          <strong>{formatCurrency(estimate.subtotal)}</strong>
        </div>
      </div>
    </section>
  );
}

function configurePreviewRenderer(
  renderer: THREE.WebGLRenderer,
  maxPixelRatio: number,
  shadows: boolean,
) {
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxPixelRatio));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  renderer.shadowMap.enabled = shadows;
  if (shadows) renderer.shadowMap.type = THREE.PCFShadowMap;
}

function addPreviewLights(
  scene: THREE.Scene,
  shadows: boolean,
  presentation: 'standard' | 'card' = 'standard',
) {
  const isCard = presentation === 'card';
  scene.add(new THREE.HemisphereLight(0xfff4df, 0x18251f, isCard ? 1.05 : 2.1));
  const keyLight = new THREE.DirectionalLight(0xffefd0, isCard ? 4.6 : 3.8);
  keyLight.position.set(isCard ? -3.5 : 4, 5, isCard ? 3.5 : 6);
  keyLight.castShadow = shadows;
  if (shadows) keyLight.shadow.mapSize.set(1024, 1024);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xbad9d0, isCard ? 0.65 : 1.5);
  fillLight.position.set(isCard ? 4 : -5, isCard ? 0.5 : 1, 3);
  scene.add(fillLight);

  if (isCard) {
    const rimLight = new THREE.DirectionalLight(0xd99a62, 1.4);
    rimLight.position.set(0, 5, -3);
    scene.add(rimLight);
  }
}

function positionPreviewModel(
  model: THREE.Group,
  presentation: 'editor' | 'showcase' | 'hero' | 'card' = 'editor',
) {
  model.rotation.x = presentation === 'card' ? 0.16 : presentation === 'editor' ? -0.12 : -0.08;
  model.rotation.y =
    presentation === 'hero'
      ? 0
      : presentation === 'showcase'
        ? -0.08
        : presentation === 'card'
          ? -0.12
          : -0.26;
}

function fitCardPreviewCamera(camera: THREE.PerspectiveCamera, modelRoot: THREE.Group) {
  camera.position.copy(CARD_CAMERA_POSITION);
  camera.lookAt(CARD_CAMERA_TARGET);
  camera.updateMatrixWorld(true);
  modelRoot.updateMatrixWorld(true);

  const worldCorners: THREE.Vector3[] = [];
  modelRoot.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    const meshGeometry = object.geometry;
    if (!meshGeometry.boundingBox) meshGeometry.computeBoundingBox();
    const bounds = meshGeometry.boundingBox;
    if (!bounds) return;

    for (const x of [bounds.min.x, bounds.max.x]) {
      for (const y of [bounds.min.y, bounds.max.y]) {
        for (const z of [bounds.min.z, bounds.max.z]) {
          worldCorners.push(new THREE.Vector3(x, y, z).applyMatrix4(object.matrixWorld));
        }
      }
    }
  });
  if (worldCorners.length === 0) return;

  const viewDirection = CARD_CAMERA_POSITION.clone().sub(CARD_CAMERA_TARGET).normalize();
  let distance = CARD_CAMERA_POSITION.distanceTo(CARD_CAMERA_TARGET);

  for (let iteration = 0; iteration < 4; iteration += 1) {
    let maxProjectedX = 0;
    let maxProjectedY = 0;

    for (const corner of worldCorners) {
      const projected = corner.clone().project(camera);
      maxProjectedX = Math.max(maxProjectedX, Math.abs(projected.x));
      maxProjectedY = Math.max(maxProjectedY, Math.abs(projected.y));
    }

    const overflow = Math.max(
      maxProjectedX / CARD_SAFE_NDC_EXTENT,
      maxProjectedY / CARD_SAFE_NDC_EXTENT,
    );
    if (overflow <= 1) return;

    distance *= overflow * 1.025;
    camera.position.copy(CARD_CAMERA_TARGET).addScaledVector(viewDirection, distance);
    camera.lookAt(CARD_CAMERA_TARGET);
    camera.updateMatrixWorld(true);
  }
}

function createDoorModel(
  config: ConfiguratorState,
  { castShadows = true }: { castShadows?: boolean } = {},
) {
  const materialDefinition = getMaterial(config.materialId);
  const workpieceMaterial = new THREE.MeshPhysicalMaterial({
    color: materialDefinition.color,
    roughness: config.materialId === 'wood' ? 0.5 : 0.32,
    metalness: config.materialId === 'zinc' || config.materialId === 'aluminium' ? 0.72 : 0.03,
    clearcoat: config.finishId === 'matte' ? 0.18 : config.finishId === 'oil' ? 0.42 : 0.08,
    clearcoatRoughness: 0.55,
    side: THREE.DoubleSide,
  });

  let surfaceGeometry: THREE.BufferGeometry;
  try {
    surfaceGeometry = createDoorSurfaceGeometry(config);
  } catch (error) {
    workpieceMaterial.dispose();
    throw error;
  }
  const group = new THREE.Group();
  const slab = new THREE.Mesh(surfaceGeometry, workpieceMaterial);
  slab.castShadow = castShadows;
  slab.receiveShadow = castShadows;
  group.add(slab);

  return group;
}

function disposeChildren(object: THREE.Object3D) {
  while (object.children.length) {
    const child = object.children[0];
    object.remove(child);
    child.traverse(descendant => disposeRenderable(descendant));
  }
}

function disposeRenderable(object: THREE.Object3D) {
  if (!(object instanceof THREE.Mesh || object instanceof THREE.Line)) return;
  object.geometry?.dispose();
  const materials = Array.isArray(object.material) ? object.material : [object.material];
  materials.forEach(material => material?.dispose());
}

function formatMm(value: number) {
  return new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 1 }).format(value);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(value);
}
